import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CallsignSchedule,
  CallsignScheduleCallsignsItem,
  CallsignSchedulePeriod,
} from '@transreport/pauk-rrms-api'
import {
  getBaselineCallsignSchedule,
  getEffectiveCallsignSchedulePeriods,
  setBaselineCallsignSchedule,
  setCallsignScheduleOverride,
} from '@transreport/pauk-rrms-api'
import { addDays, format, isBefore, isSameDay, parseISO, startOfDay } from 'date-fns'
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ChevronsDown,
  Clock3,
  GripHorizontal,
  UserRound,
  X,
} from 'lucide-react'
import type React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppButton, Modal, SkeletonBlock, StaffAutocomplete } from '@/components/framework'
import { useOperatingStations } from '@/hooks/useOperatingStations'
import { clamp, DAY_MINUTES, minutesToHHmm, parseTimeToMinutes } from '@/lib/timeline/time'

import { getCallsignColourClass, getEffectivePeriodForDate } from './shiftScheduleVisualisation'

type ShiftsAgendaDrawerProps = {
  isOpen: boolean
  selectedDate: Date
  onDateChange: (date: Date) => void
  onClose: () => void
}

type AgendaTimes = {
  start: number
  end: number
}

type DragMode = 'move' | 'resize-start' | 'resize-end'

type DragSession = {
  callsign: CallsignScheduleCallsignsItem
  mode: DragMode
  pointerStartY: number
  original: AgendaTimes
  current: AgendaTimes
}

type PendingChange = {
  callsign: CallsignScheduleCallsignsItem
  kind: 'assignment' | 'time'
  next: CallsignScheduleCallsignsItem
}

type ScheduleApplicability = 'all_days' | 'weekdays' | 'weekends'
type ChangeScope = 'default' | 'override'

const HOUR_HEIGHT = 56
const NEXT_DAY_HOUR_HEIGHT = 14
const MIN_SHIFT_MINUTES = 15
const SNAP_MINUTES = 15
const MAX_SHIFT_MINUTES = DAY_MINUTES
const MIN_LANE_WIDTH = 180
const AGENDA_SKELETON_LANES = Array.from({ length: 3 }, (_, index) => index)

const ShiftsAgendaSkeleton = ({ label }: { label: string }) => (
  <div role="status" aria-label={label} className="min-h-0 flex-1 overflow-hidden bg-white p-4">
    <div className="ml-16 grid grid-cols-3 gap-2 border-b border-gray-200 pb-3">
      {AGENDA_SKELETON_LANES.map((lane) => (
        <SkeletonBlock key={lane} className="h-5 w-20" />
      ))}
    </div>
    <div className="mt-3 flex h-full gap-2">
      <div className="w-14 space-y-10 pt-1">
        {AGENDA_SKELETON_LANES.map((labelIndex) => (
          <SkeletonBlock key={labelIndex} className="h-3 w-10" />
        ))}
      </div>
      {AGENDA_SKELETON_LANES.map((lane) => (
        <div key={lane} className="relative flex-1 border-x border-gray-100">
          <SkeletonBlock
            className={`absolute inset-x-2 h-36 ${lane === 1 ? 'top-24' : 'top-10'}`}
          />
        </div>
      ))}
    </div>
  </div>
)

function getAgendaTimes(callsign: CallsignScheduleCallsignsItem): AgendaTimes {
  const start = parseTimeToMinutes(callsign.start_time) ?? 0
  const rawEnd = parseTimeToMinutes(callsign.end_time) ?? start + MIN_SHIFT_MINUTES
  const end = callsign.ends_next_day || rawEnd < start ? rawEnd + DAY_MINUTES : rawEnd
  return { start, end }
}

function minutesToScheduleTime(minutes: number): string {
  return minutesToHHmm(minutes % DAY_MINUTES)
}

function snapMinutes(minutes: number): number {
  return Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES
}

function adjustDragRange(session: DragSession, deltaMinutes: number): AgendaTimes {
  const { original, mode } = session

  if (mode === 'move') {
    const duration = original.end - original.start
    const nextStart = clamp(original.start + deltaMinutes, 0, DAY_MINUTES - MIN_SHIFT_MINUTES)
    return { start: nextStart, end: nextStart + duration }
  }

  if (mode === 'resize-start') {
    return {
      start: clamp(
        original.start + deltaMinutes,
        0,
        Math.min(DAY_MINUTES - MIN_SHIFT_MINUTES, original.end - MIN_SHIFT_MINUTES)
      ),
      end: original.end,
    }
  }

  return {
    start: original.start,
    end: clamp(
      original.end + deltaMinutes,
      original.start + MIN_SHIFT_MINUTES,
      Math.min(original.start + MAX_SHIFT_MINUTES, DAY_MINUTES * 2)
    ),
  }
}

function getApplicability(period: CallsignSchedulePeriod): ScheduleApplicability {
  return (period.applicability ??
    period.schedule.applicability ??
    'all_days') as ScheduleApplicability
}

function replaceCallsign(
  schedule: CallsignSchedule,
  target: CallsignScheduleCallsignsItem,
  next: CallsignScheduleCallsignsItem
) {
  const normalisedName = target.name.trim().toLowerCase()
  let found = false
  const callsigns = schedule.callsigns.map((callsign, position) => {
    const isMatch =
      callsign.id === target.id || callsign.name.trim().toLowerCase() === normalisedName
    if (isMatch) found = true

    const source = isMatch ? next : callsign
    return {
      name: source.name,
      start_time: source.start_time,
      end_time: source.end_time,
      user_id: source.user_id ?? null,
      position,
    }
  })

  if (!found) throw new Error('Callsign not found in schedule')
  return callsigns
}

function getAgendaPosition(minutes: number): number {
  const sameDayMinutes = Math.min(minutes, DAY_MINUTES)
  const nextDayMinutes = Math.max(0, minutes - DAY_MINUTES)
  return (sameDayMinutes / 60) * HOUR_HEIGHT + (nextDayMinutes / 60) * NEXT_DAY_HOUR_HEIGHT
}

function getAgendaMinutesAtPosition(position: number): number {
  const dayHeight = (DAY_MINUTES / 60) * HOUR_HEIGHT
  if (position <= dayHeight) return (position / HOUR_HEIGHT) * 60
  return DAY_MINUTES + ((position - dayHeight) / NEXT_DAY_HOUR_HEIGHT) * 60
}

export const ShiftsAgendaDrawer: React.FC<ShiftsAgendaDrawerProps> = ({
  isOpen,
  selectedDate,
  onDateChange,
  onClose,
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const today = useMemo(() => startOfDay(new Date()), [])
  const [previewTimes, setPreviewTimes] = useState<Record<string, AgendaTimes>>({})
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null)
  const [selectedScope, setSelectedScope] = useState<ChangeScope>('override')
  const dragSessionRef = useRef<DragSession | null>(null)
  const timelineScrollRef = useRef<HTMLDivElement>(null)
  const drawerRef = useRef<HTMLElement>(null)
  const drawerTitleRef = useRef<HTMLHeadingElement>(null)
  const invokerRef = useRef<HTMLElement | null>(null)
  const { selectedStationCode } = useOperatingStations()

  const dateKey = format(selectedDate, 'yyyy-MM-dd')
  const activeUntil = format(addDays(selectedDate, 1), 'yyyy-MM-dd')
  const isPast = isBefore(selectedDate, today)

  const periodsQuery = useQuery({
    queryKey: ['callsignSchedule', 'agenda', dateKey, activeUntil, selectedStationCode],
    queryFn: () =>
      getEffectiveCallsignSchedulePeriods({
        active_from: dateKey,
        active_until: activeUntil,
        station_code: selectedStationCode ?? '',
      }),
    enabled: isOpen && Boolean(selectedStationCode),
  })

  const periods = periodsQuery.data ?? []
  const period = getEffectivePeriodForDate(periods, selectedDate)
  const callsigns = useMemo(
    () =>
      (period?.schedule.callsigns ?? []).toSorted((left, right) => left.position - right.position),
    [period?.schedule.callsigns]
  )

  const saveScheduleMutation = useMutation({
    mutationKey: ['callsignSchedule', 'agenda', 'save'],
    meta: {
      analytics: {
        event: 'schedule_saved',
        area: 'schedule',
        parameters: () => ({
          schedule_type: selectedScope === 'default' ? 'baseline' : 'override',
        }),
      },
      toast: {
        success: t('resources.agenda.editPrompt.success'),
        error: t('resources.agenda.editPrompt.error'),
      },
    },
    mutationFn: async () => {
      if (!selectedStationCode || !period || !pendingChange) return

      if (selectedScope === 'default') {
        const baseline = await getBaselineCallsignSchedule({
          on: dateKey,
          station_code: selectedStationCode,
        })
        await setBaselineCallsignSchedule(dateKey, {
          station_code: selectedStationCode,
          applicability: baseline.applicability ?? 'all_days',
          callsigns: replaceCallsign(baseline, pendingChange.callsign, pendingChange.next),
        })
      } else {
        await setCallsignScheduleOverride(dateKey, {
          active_until: activeUntil,
          station_code: selectedStationCode,
          applicability: getApplicability(period),
          callsigns: replaceCallsign(period.schedule, pendingChange.callsign, pendingChange.next),
        })
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['callsignSchedule'] })
      setPreviewTimes({})
      setPendingChange(null)
    },
  })
  const isSaving = saveScheduleMutation.isPending

  const showDay = (nextDate: Date) => {
    onDateChange(startOfDay(nextDate))
  }

  const clearPendingChange = () => {
    if (isSaving) return
    if (pendingChange) {
      setPreviewTimes((current) => {
        const next = { ...current }
        delete next[pendingChange.callsign.id]
        return next
      })
    }
    setPendingChange(null)
  }

  const beginDrag = (
    event: React.PointerEvent,
    callsign: CallsignScheduleCallsignsItem,
    mode: DragMode
  ) => {
    if (event.button !== 0 || isPast || isSaving) return
    event.preventDefault()
    event.stopPropagation()
    const original = previewTimes[callsign.id] ?? getAgendaTimes(callsign)
    dragSessionRef.current = {
      callsign,
      mode,
      pointerStartY: event.clientY,
      original,
      current: original,
    }
  }

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const session = dragSessionRef.current
      if (!session) return
      event.preventDefault()
      const deltaY = event.clientY - session.pointerStartY
      const deltaMinutes = snapMinutes(
        session.mode === 'resize-end'
          ? getAgendaMinutesAtPosition(getAgendaPosition(session.original.end) + deltaY) -
              session.original.end
          : (deltaY / HOUR_HEIGHT) * 60
      )
      const current = adjustDragRange(session, deltaMinutes)
      session.current = current
      setPreviewTimes((existing) => ({ ...existing, [session.callsign.id]: current }))
    }

    const handlePointerEnd = () => {
      const session = dragSessionRef.current
      dragSessionRef.current = null
      if (!session) return

      const hasMoved =
        session.current.start !== session.original.start ||
        session.current.end !== session.original.end
      if (!hasMoved) {
        setPreviewTimes((current) => {
          const next = { ...current }
          delete next[session.callsign.id]
          return next
        })
        return
      }

      setSelectedScope('override')
      setPendingChange({
        callsign: session.callsign,
        kind: 'time',
        next: {
          ...session.callsign,
          start_time: minutesToScheduleTime(session.current.start),
          end_time: minutesToScheduleTime(session.current.end),
        },
      })
    }

    window.addEventListener('pointermove', handlePointerMove, { passive: false })
    window.addEventListener('pointerup', handlePointerEnd)
    window.addEventListener('pointercancel', handlePointerEnd)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerEnd)
      window.removeEventListener('pointercancel', handlePointerEnd)
    }
  }, [])

  useEffect(() => {
    setPreviewTimes({})
    setPendingChange(null)
    if (!isOpen) return
    requestAnimationFrame(() => {
      if (timelineScrollRef.current) timelineScrollRef.current.scrollTop = HOUR_HEIGHT * 6
    })
  }, [dateKey, isOpen, selectedStationCode])

  useEffect(() => {
    if (!isOpen) return
    invokerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const calendarHeader = document.querySelector<HTMLElement>('[data-shifts-calendar-header]')
    const calendarBackground = document.querySelector<HTMLElement>('[data-calendar-background]')
    calendarHeader?.setAttribute('inert', '')
    calendarBackground?.setAttribute('inert', '')
    window.requestAnimationFrame(() => drawerTitleRef.current?.focus())

    return () => {
      calendarHeader?.removeAttribute('inert')
      calendarBackground?.removeAttribute('inert')
      const invoker = invokerRef.current
      if (invoker?.isConnected) invoker.focus()
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || pendingChange) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, pendingChange])

  const openStaffEditor = (callsign: CallsignScheduleCallsignsItem) => {
    if (isPast || isSaving) return
    setSelectedScope('override')
    setPendingChange({ callsign, kind: 'assignment', next: { ...callsign } })
  }

  const openTimeEditor = (callsign: CallsignScheduleCallsignsItem) => {
    if (isPast || isSaving) return
    setSelectedScope('override')
    setPendingChange({ callsign, kind: 'time', next: { ...callsign } })
  }

  const handleDrawerKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Tab' || pendingChange || !drawerRef.current) return
    const focusable = Array.from(
      drawerRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    )
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (
      event.shiftKey &&
      (document.activeElement === first || document.activeElement === drawerTitleRef.current)
    ) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const handleSave = () => {
    if (!selectedStationCode || !period || !pendingChange) return
    saveScheduleMutation.mutate()
  }

  const agendaCallsigns = callsigns.map((callsign) => ({
    callsign,
    times: previewTimes[callsign.id] ?? getAgendaTimes(callsign),
  }))
  const timelineEndMinutes = Math.max(
    DAY_MINUTES,
    ...agendaCallsigns.map(({ times }) => Math.ceil(times.end / 60) * 60)
  )
  const timelineHeight = getAgendaPosition(timelineEndMinutes)
  const hourMarkers = Array.from({ length: DAY_MINUTES / 60 + 1 }, (_, hour) => hour * 60)
  const laneWidth = Math.max(callsigns.length * MIN_LANE_WIDTH, 640)
  const currentMinutes = new Date().getHours() * 60 + new Date().getMinutes()
  const applicationClass =
    period && getApplicability(period) === 'weekends'
      ? 'bg-amber-50/40'
      : period && getApplicability(period) === 'weekdays'
        ? 'bg-emerald-50/40'
        : 'bg-white'

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`absolute inset-0 z-40 cursor-default bg-black/25 transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />
      {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the dialog owns Escape and focus trapping */}
      <aside
        ref={drawerRef}
        role="dialog"
        aria-modal={isOpen ? 'true' : undefined}
        aria-labelledby="shifts-agenda-drawer-title"
        aria-hidden={!isOpen}
        inert={!isOpen}
        onKeyDown={handleDrawerKeyDown}
        className={`absolute inset-y-0 right-0 z-50 flex w-full max-w-3xl flex-col bg-gray-100 shadow-2xl transition-transform duration-200 ease-out ${
          isOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full'
        }`}
      >
        <header className="z-20 border-b border-gray-200 bg-white px-4 py-3 shadow-sm sm:px-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2
                ref={drawerTitleRef}
                id="shifts-agenda-drawer-title"
                tabIndex={-1}
                className="truncate text-lg font-semibold text-gray-900"
              >
                {format(selectedDate, 'EEEE d MMMM yyyy')}
              </h2>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                <span>{t('resources.agenda.dragHint')}</span>
                {period?.schedule.schedule_type === 'override' && (
                  <span className="font-medium text-purple-700">
                    {t('resources.agenda.overrideActive')}
                  </span>
                )}
                {isPast && (
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-gray-600">
                    {t('resources.agenda.pastReadOnly')}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
              aria-label={t('resources.agenda.close')}
            >
              <X className="size-5" aria-hidden="true" />
            </button>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => showDay(addDays(selectedDate, -1))}
              className="inline-flex size-9 cursor-pointer items-center justify-center rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
              aria-label={t('resources.agenda.previousDay')}
            >
              <ChevronLeft className="size-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => showDay(today)}
              className="inline-flex h-9 cursor-pointer items-center gap-2 rounded border border-gray-300 bg-white px-3 text-sm font-medium text-gray-800 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
            >
              <CalendarDays className="size-4" aria-hidden="true" />
              {t('resources.agenda.today')}
            </button>
            <button
              type="button"
              onClick={() => showDay(addDays(selectedDate, 1))}
              className="inline-flex size-9 cursor-pointer items-center justify-center rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
              aria-label={t('resources.agenda.nextDay')}
            >
              <ChevronRight className="size-4" aria-hidden="true" />
            </button>
            <label htmlFor="shifts-agenda-date" className="sr-only">
              {t('resources.agenda.jumpToDate')}
            </label>
            <input
              id="shifts-agenda-date"
              type="date"
              value={dateKey}
              onChange={(event) => {
                if (event.target.value) showDay(parseISO(event.target.value))
              }}
              className="h-9 rounded border border-gray-300 px-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-2 sm:px-5">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{t('resources.agenda.title')}</h3>
            </div>
            <span className="text-xs text-gray-500">{selectedStationCode}</span>
          </div>

          {!selectedStationCode && (
            <div className="m-6 rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-700 shadow-sm">
              {t('common.selectStationFirst')}
            </div>
          )}

          {selectedStationCode && periodsQuery.error && (
            <div
              role="alert"
              className="m-6 rounded-lg border border-red-300 bg-red-50 p-6 text-sm text-red-800"
            >
              {t('resources.agenda.loadError')}
            </div>
          )}

          {selectedStationCode &&
            !periodsQuery.error &&
            callsigns.length === 0 &&
            !periodsQuery.isLoading && (
              <div className="m-6 rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-700 shadow-sm">
                {t('resources.agenda.empty')}
              </div>
            )}

          {selectedStationCode &&
            !periodsQuery.error &&
            (periodsQuery.isLoading || callsigns.length > 0) && (
              <div
                ref={timelineScrollRef}
                className="min-h-0 flex-1 overflow-auto bg-white"
                aria-busy={periodsQuery.isLoading}
              >
                {periodsQuery.isLoading ? (
                  <ShiftsAgendaSkeleton label={t('resources.agenda.loading')} />
                ) : (
                  <div
                    className={`relative min-w-max ${applicationClass}`}
                    style={{ width: laneWidth + 64 }}
                  >
                    <div
                      className="sticky top-0 z-30 ml-16 grid h-11 border-b border-gray-300 bg-white/95 shadow-sm backdrop-blur"
                      style={{
                        gridTemplateColumns: `repeat(${callsigns.length}, minmax(${MIN_LANE_WIDTH}px, 1fr))`,
                      }}
                    >
                      {callsigns.map((callsign) => (
                        <div
                          key={callsign.id}
                          className="flex items-center justify-start border-r border-gray-200 px-3 text-left text-sm font-semibold text-gray-800"
                        >
                          {callsign.name}
                        </div>
                      ))}
                    </div>

                    <div
                      className="relative ml-16"
                      style={{ height: timelineHeight, width: laneWidth }}
                      aria-label={t('resources.agenda.timelineAriaLabel')}
                    >
                      {hourMarkers.map((minutes) => (
                        <div
                          key={minutes}
                          className="pointer-events-none absolute inset-x-0 border-t border-gray-200"
                          style={{ top: getAgendaPosition(minutes) }}
                        >
                          <span className="absolute -top-2.5 -left-16 w-14 pr-2 text-right text-xs font-medium text-gray-500">
                            {minutes === DAY_MINUTES
                              ? timelineEndMinutes > DAY_MINUTES
                                ? '00:00'
                                : '24:00'
                              : minutesToHHmm(minutes)}
                          </span>
                        </div>
                      ))}

                      {timelineEndMinutes > DAY_MINUTES && (
                        <>
                          <div
                            className="pointer-events-none absolute inset-x-0 bg-gradient-to-b from-indigo-50/70 to-white"
                            style={{
                              top: getAgendaPosition(DAY_MINUTES),
                              height: timelineHeight - getAgendaPosition(DAY_MINUTES),
                            }}
                          />
                          <div
                            className="pointer-events-none absolute inset-x-0 z-20 border-t-2 border-indigo-400"
                            style={{ top: getAgendaPosition(DAY_MINUTES) }}
                          >
                            <span className="absolute top-1 left-2 inline-flex items-center gap-1 rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700">
                              <ChevronsDown className="size-3.5" aria-hidden="true" />
                              {t('resources.agenda.nextDay')}
                            </span>
                          </div>
                          <div
                            className="pointer-events-none absolute inset-x-0 z-20 border-t border-indigo-300"
                            style={{ top: timelineHeight }}
                          >
                            <span className="absolute -top-2.5 -left-16 w-14 pr-2 text-right text-[11px] font-semibold text-indigo-700">
                              {minutesToScheduleTime(timelineEndMinutes)}
                            </span>
                          </div>
                        </>
                      )}

                      {callsigns.map((callsign, index) => (
                        <div
                          key={`lane-${callsign.id}`}
                          className="pointer-events-none absolute inset-y-0 border-r border-gray-200"
                          style={{
                            left: `${(index / callsigns.length) * 100}%`,
                            width: `${100 / callsigns.length}%`,
                          }}
                        />
                      ))}

                      {isSameDay(selectedDate, today) && currentMinutes <= timelineEndMinutes && (
                        <div
                          className="pointer-events-none absolute inset-x-0 z-20 border-t-2 border-red-500"
                          style={{ top: getAgendaPosition(currentMinutes) }}
                          aria-label={t('resources.agenda.currentTime')}
                        >
                          <span className="absolute -top-1.5 -left-1 size-3 rounded-full bg-red-500" />
                        </div>
                      )}

                      {agendaCallsigns.map(({ callsign, times }, index) => {
                        const top = getAgendaPosition(times.start)
                        const height = Math.max(
                          getAgendaPosition(times.end) - getAgendaPosition(times.start),
                          30
                        )
                        const endLabel = minutesToScheduleTime(times.end)
                        const isOvernight = times.end > DAY_MINUTES

                        return (
                          <article
                            key={callsign.id}
                            aria-label={`${t('resources.agenda.shiftAriaLabel', {
                              name: callsign.name,
                              start: minutesToScheduleTime(times.start),
                              end: endLabel,
                            })}${isOvernight ? `, ${t('resources.overnight')}` : ''}`}
                            onPointerDown={(event) => beginDrag(event, callsign, 'move')}
                            className={`absolute z-10 mx-1 overflow-hidden rounded-md border px-2 py-1.5 shadow-sm select-none ${isPast ? 'cursor-default opacity-70' : 'cursor-grab active:cursor-grabbing'} ${getCallsignColourClass(callsign.name)}`}
                            style={{
                              top,
                              height,
                              left: `calc(${(index / callsigns.length) * 100}% + 4px)`,
                              width: `calc(${100 / callsigns.length}% - 8px)`,
                            }}
                          >
                            {!isPast && (
                              <button
                                type="button"
                                onPointerDown={(event) =>
                                  beginDrag(event, callsign, 'resize-start')
                                }
                                className="absolute inset-x-0 top-0 flex h-6 cursor-ns-resize items-start justify-center rounded-t opacity-60 hover:bg-black/10 hover:opacity-100"
                                aria-label={t('resources.agenda.resizeStart', {
                                  name: callsign.name,
                                })}
                              >
                                <GripHorizontal className="h-2.5 w-4" aria-hidden="true" />
                              </button>
                            )}
                            <div className="mt-1 flex h-full min-h-0 flex-col overflow-hidden">
                              <div className="flex items-center justify-between gap-1 text-xs font-semibold">
                                <span className="flex min-w-0 items-center gap-1">
                                  <span>{callsign.name}</span>
                                  {isOvernight && (
                                    <span className="inline-flex items-center gap-0.5 rounded bg-white/70 px-1 py-0.5 text-[10px] font-medium">
                                      <ChevronsDown className="size-3" aria-hidden="true" />
                                      {t('resources.overnight')}
                                    </span>
                                  )}
                                </span>
                                <span className="font-medium whitespace-nowrap">
                                  {minutesToScheduleTime(times.start)}–{endLabel}
                                  {isOvernight && (
                                    <span
                                      className="ml-1 inline-flex align-middle"
                                      aria-label={t('resources.continuesNextDay')}
                                    >
                                      <ChevronsDown className="size-3.5" aria-hidden="true" />
                                    </span>
                                  )}
                                </span>
                              </div>
                              <button
                                type="button"
                                disabled={isPast || isSaving}
                                onPointerDown={(event) => event.stopPropagation()}
                                onClick={() => openStaffEditor(callsign)}
                                className="mt-1 inline-flex min-w-0 cursor-pointer items-center gap-1 self-start rounded bg-white/60 px-1.5 py-1 text-left text-xs hover:bg-white/90 focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none disabled:cursor-default"
                                aria-label={t('resources.agenda.assignStaff', {
                                  name: callsign.name,
                                })}
                              >
                                <UserRound className="size-3 shrink-0" aria-hidden="true" />
                                <span className="truncate">
                                  {callsign.user_name || t('resources.agenda.unassigned')}
                                </span>
                              </button>
                              <button
                                type="button"
                                disabled={isPast || isSaving}
                                onPointerDown={(event) => event.stopPropagation()}
                                onClick={() => openTimeEditor(callsign)}
                                className="mt-1 inline-flex min-w-0 cursor-pointer items-center gap-1 self-start rounded bg-white/60 px-1.5 py-1 text-left text-xs hover:bg-white/90 disabled:cursor-default"
                                aria-label={t('resources.agenda.editTimes', {
                                  name: callsign.name,
                                })}
                              >
                                <Clock3 className="size-3 shrink-0" aria-hidden="true" />
                                <span>{t('resources.agenda.editTimesShort')}</span>
                              </button>
                            </div>
                            {isOvernight && (
                              <span
                                aria-hidden="true"
                                data-agenda-continuation
                                className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center bg-gradient-to-b from-transparent via-white/40 to-white/80"
                                style={{ top: getAgendaPosition(DAY_MINUTES) - top }}
                              >
                                <ChevronsDown className="size-5 opacity-60" />
                              </span>
                            )}
                            {!isPast && (
                              <button
                                type="button"
                                onPointerDown={(event) => beginDrag(event, callsign, 'resize-end')}
                                className="absolute inset-x-0 bottom-0 flex h-6 cursor-ns-resize items-end justify-center rounded-b opacity-60 hover:bg-black/10 hover:opacity-100"
                                aria-label={t('resources.agenda.resizeEnd', {
                                  name: callsign.name,
                                })}
                              >
                                <GripHorizontal className="h-2.5 w-4" aria-hidden="true" />
                              </button>
                            )}
                          </article>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

          <Modal
            isOpen={Boolean(pendingChange)}
            onClose={clearPendingChange}
            title={t('resources.agenda.editPrompt.title')}
            size="sm"
          >
            {pendingChange && (
              <div className="space-y-4">
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <p className="font-semibold text-gray-900">{pendingChange.callsign.name}</p>
                  <p className="mt-1 text-sm text-gray-700">
                    {pendingChange.next.start_time}–{pendingChange.next.end_time}
                  </p>
                </div>

                {pendingChange.kind === 'assignment' && selectedStationCode && (
                  <div>
                    <label
                      className="mb-1 block text-sm font-medium text-gray-700"
                      htmlFor="agenda-staff-assignment"
                    >
                      {t('resources.agenda.editPrompt.staffLabel')}
                    </label>
                    <StaffAutocomplete
                      id="agenda-staff-assignment"
                      name="agenda-staff-assignment-value"
                      value={pendingChange.next.user_name ?? ''}
                      userId={pendingChange.next.user_id}
                      stationCode={selectedStationCode}
                      placeholder={t('resources.table.staffPlaceholder')}
                      onChange={(userId, displayName) =>
                        setPendingChange((current) =>
                          current
                            ? {
                                ...current,
                                next: { ...current.next, user_id: userId, user_name: displayName },
                              }
                            : current
                        )
                      }
                      onClear={() =>
                        setPendingChange((current) =>
                          current
                            ? {
                                ...current,
                                next: { ...current.next, user_id: null, user_name: null },
                              }
                            : current
                        )
                      }
                    />
                  </div>
                )}

                {pendingChange.kind === 'time' && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="agenda-shift-start"
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        {t('resources.agenda.editPrompt.startLabel')}
                      </label>
                      <input
                        id="agenda-shift-start"
                        type="time"
                        value={pendingChange.next.start_time}
                        onChange={(event) =>
                          setPendingChange((current) =>
                            current
                              ? {
                                  ...current,
                                  next: { ...current.next, start_time: event.target.value },
                                }
                              : current
                          )
                        }
                        className="w-full rounded border border-border-gray px-3 py-2"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="agenda-shift-end"
                        className="mb-1 block text-sm font-medium text-gray-700"
                      >
                        {t('resources.agenda.editPrompt.endLabel')}
                      </label>
                      <input
                        id="agenda-shift-end"
                        type="time"
                        value={pendingChange.next.end_time}
                        onChange={(event) =>
                          setPendingChange((current) =>
                            current
                              ? {
                                  ...current,
                                  next: { ...current.next, end_time: event.target.value },
                                }
                              : current
                          )
                        }
                        className="w-full rounded border border-border-gray px-3 py-2"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {t('resources.agenda.editPrompt.scopeTitle')}
                  </p>
                  <p className="mt-1 text-xs text-gray-600">
                    {t('resources.agenda.editPrompt.scopeHint')}
                  </p>
                </div>

                <div className="grid items-stretch gap-3 sm:grid-cols-2">
                  {(['override', 'default'] as const).map((scope) => {
                    const isSelected = selectedScope === scope
                    const isOverride = scope === 'override'
                    return (
                      <label
                        key={scope}
                        aria-label={t(
                          isOverride
                            ? 'resources.agenda.editPrompt.oneOff'
                            : 'resources.agenda.editPrompt.default'
                        )}
                        className={`flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 text-left transition-colors focus-within:ring-2 focus-within:ring-primary focus-within:outline-none ${
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-gray-300 bg-white hover:bg-gray-50'
                        } ${isSaving ? 'cursor-not-allowed opacity-50' : ''}`}
                      >
                        <input
                          type="radio"
                          name="agenda-change-scope"
                          value={scope}
                          checked={isSelected}
                          disabled={isSaving}
                          onChange={() => setSelectedScope(scope)}
                          className="mt-0.5 size-4 shrink-0 accent-primary"
                        />
                        <span className="min-w-0">
                          <span
                            className={`block text-sm leading-5 font-semibold ${
                              isSelected ? 'text-primary' : 'text-gray-900'
                            }`}
                          >
                            {t(
                              isOverride
                                ? 'resources.agenda.editPrompt.oneOff'
                                : 'resources.agenda.editPrompt.default'
                            )}
                          </span>
                          <span className="mt-1 block text-xs leading-4 text-gray-600">
                            {t(
                              isOverride
                                ? 'resources.agenda.editPrompt.oneOffDescription'
                                : 'resources.agenda.editPrompt.defaultDescription'
                            )}
                          </span>
                        </span>
                      </label>
                    )
                  })}
                </div>

                <div className="flex items-center justify-end gap-2 border-t border-gray-200 pt-4">
                  <AppButton
                    variant="outline"
                    size="sm"
                    onClick={clearPendingChange}
                    disabled={isSaving}
                  >
                    {t('resources.agenda.editPrompt.cancel')}
                  </AppButton>
                  <AppButton
                    variant="primary"
                    size="sm"
                    onClick={() => void handleSave()}
                    loading={isSaving}
                  >
                    {t('resources.agenda.editPrompt.submit')}
                  </AppButton>
                </div>
              </div>
            )}
          </Modal>
        </div>
      </aside>
    </>
  )
}
